/**
 * Service Worker Registration Utility
 * Handles PWA service worker registration and lifecycle
 */

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export async function registerServiceWorker(config?: ServiceWorkerConfig) {
  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Workers are not supported in this browser');
    return null;
  }

  // Only register service worker in production or on localhost
  // Use import.meta.env for Vite compatibility
  if (!import.meta.env.PROD && !isLocalhost) {
    console.warn('Service Worker registration skipped (not production)');
    return null;
  }

  try {
    // Wait for page load to avoid resource contention
    if (document.readyState === 'loading') {
      await new Promise<void>((resolve) => {
        window.addEventListener('load', () => resolve());
      });
    }


    // Register the service worker
    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      {
        scope: '/',
      }
    );


    // Check for updates periodically
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;


      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New version available
            if (config?.onUpdate) {
              config.onUpdate(registration);
            }
          } else {
            // First install
            if (config?.onSuccess) {
              config.onSuccess(registration);
            }
          }
        }
      });
    });

    // Poll for updates every hour
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    return registration;
  } catch (error) {
    const err = error as Error;
    console.error('[SW] Registration failed:', err);

    if (config?.onError) {
      config.onError(err);
    }

    return null;
  }
}

export async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const success = await registration.unregister();

    if (success) {
      // console.log('[SW] Service worker unregistered successfully');
    }

    return success;
  } catch (error) {
    console.error('[SW] Unregistration failed:', error);
    return false;
  }
}

export function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) {
    return Promise.resolve(undefined);
  }

  return navigator.serviceWorker.ready;
}

export function isServiceWorkerActive(): boolean {
  return (
    'serviceWorker' in navigator &&
    navigator.serviceWorker.controller !== null
  );
}
